import { useState, useMemo, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Search, Star, X, Lock, SlidersHorizontal } from 'lucide-react';
import { fetchRecipes, Recipe, CATEGORIES } from '@/lib/recipes';
import { useAuth } from '@/contexts/AuthContext';
import LoadingSpinner from '@/components/LoadingSpinner';
import styles from './RecipesPage.module.css';

const RATING_OPTIONS = [
  { label: '4.5 & up', value: 4.5 },
  { label: '4.0 & up', value: 4.0 },
  { label: '3.5 & up', value: 3.5 },
  { label: '3.0 & up', value: 3.0 },
];

const SUBSCRIBE_URL = 'https://peapilpublishing.com/pages/peapil-health';
const FREE_USER_RECIPE_LIMIT = 30;
const PAGE_SIZE = 24;

function formatTime(minutes: number): string {
  if (!minutes) return '';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

function RecipeCard({ recipe }: { recipe: Recipe }) {
  const totalTime = (recipe.prepTime || 0) + (recipe.cookTime || 0);
  
  return (
    <Link to={`/recipes/${recipe.id}`} className={styles.card}>
      <div className={styles.imageWrapper}>
        <img
          src={recipe.image}
          alt={recipe.title}
          className={styles.image}
          loading="lazy"
          onError={(e) => {
            (e.target as HTMLImageElement).src = '/placeholder-recipe.png';
          }}
        />
        <span className={styles.categoryBadge}>{recipe.category}</span>
      </div>
      <div className={styles.cardContent}>
        <h3 className={styles.cardTitle}>{recipe.title}</h3>
        <div className={styles.cardMeta}>
          {recipe.rating > 0 && (
            <span className={styles.rating}>
              <Star size={16} fill="currentColor" />
              {recipe.rating.toFixed(1)}
            </span>
          )}
          {recipe.calories > 0 && <span className={styles.calories}>{recipe.calories} cal</span>}
          {totalTime > 0 && <span className={styles.time}>{formatTime(totalTime)}</span>}
        </div>
      </div>
    </Link>
  );
}

export default function RecipesPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCookbook, setSelectedCookbook] = useState('');
  const [selectedAuthor, setSelectedAuthor] = useState('');
  const [minRating, setMinRating] = useState(0);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const filterRef = useRef<HTMLDivElement>(null);
  const filterButtonRef = useRef<HTMLButtonElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const { hasFullAccess } = useAuth();

  const { data: recipes, isLoading, error } = useQuery({
    queryKey: ['recipes'],
    queryFn: fetchRecipes,
  });

  const cookbookOptions = useMemo(() => {
    if (!recipes) return [];
    const set = new Set<string>();
    recipes.forEach((r) => {
      if (r.cookbook && r.cookbook.trim()) set.add(r.cookbook.trim());
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [recipes]);

  const authorOptions = useMemo(() => {
    if (!recipes) return [];
    const set = new Set<string>();
    recipes.forEach((r) => {
      if (r.author && r.author.trim()) set.add(r.author.trim());
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [recipes]);

  const activeFilterCount =
    (selectedCookbook ? 1 : 0) + (selectedAuthor ? 1 : 0) + (minRating > 0 ? 1 : 0);

  const clearAllFilters = () => {
    setSelectedCookbook('');
    setSelectedAuthor('');
    setMinRating(0);
  };

  useEffect(() => {
    if (!showFilters) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) {
        setShowFilters(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowFilters(false);
        filterButtonRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showFilters]);

  const filteredRecipes = useMemo(() => {
    if (!recipes) return [];

    let filtered = recipes.filter((recipe) => {
      const matchesSearch = searchQuery === '' ||
        recipe.title.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = selectedCategory === 'All' || recipe.category === selectedCategory;
      const matchesCookbook = selectedCookbook === '' || (recipe.cookbook || '').trim() === selectedCookbook;
      const matchesAuthor = selectedAuthor === '' || (recipe.author || '').trim() === selectedAuthor;
      const matchesRating = minRating === 0 || recipe.rating >= minRating;
      return matchesSearch && matchesCategory && matchesCookbook && matchesAuthor && matchesRating;
    });

    // Limit to 30 recipes for free/null subscription users
    if (!hasFullAccess) {
      filtered = filtered.slice(0, FREE_USER_RECIPE_LIMIT);
    }

    return filtered;
  }, [recipes, searchQuery, selectedCategory, selectedCookbook, selectedAuthor, minRating, hasFullAccess]);

  // Reset the lazy-load window whenever the result set changes
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [searchQuery, selectedCategory, selectedCookbook, selectedAuthor, minRating]);

  const visibleRecipes = useMemo(
    () => filteredRecipes.slice(0, visibleCount),
    [filteredRecipes, visibleCount]
  );
  const hasMore = visibleCount < filteredRecipes.length;

  // Infinite scroll: load next batch when the sentinel comes into view
  useEffect(() => {
    if (!hasMore) return;
    const sentinel = loadMoreRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisibleCount((c) => Math.min(c + PAGE_SIZE, filteredRecipes.length));
        }
      },
      { rootMargin: '600px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, filteredRecipes.length]);

  if (isLoading) return <LoadingSpinner />;

  if (error) {
    return (
      <div className={styles.errorState}>
        <p>Unable to load recipes. Please try again.</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <h1 className={styles.title}>All Recipes</h1>
        <p className={styles.subtitle}>Browse and search our complete recipe collection</p>
      </header>

      <div className={styles.filters}>
        <div className={styles.searchRow}>
          <div className={styles.searchWrapper}>
            <Search size={20} className={styles.searchIcon} />
            <input
              type="text"
              placeholder="Search recipes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={styles.searchInput}
            />
            {searchQuery && (
              <button className={styles.clearButton} onClick={() => setSearchQuery('')}>
                <X size={18} />
              </button>
            )}
          </div>

          <div className={styles.filterContainer} ref={filterRef}>
            <button
              ref={filterButtonRef}
              type="button"
              className={`${styles.filterButton} ${activeFilterCount > 0 ? styles.filterButtonActive : ''}`}
              onClick={() => setShowFilters((s) => !s)}
              aria-label="Filter recipes"
              aria-expanded={showFilters}
            >
              <SlidersHorizontal size={20} />
              <span className={styles.filterButtonLabel}>Filters</span>
              {activeFilterCount > 0 && (
                <span className={styles.filterBadge}>{activeFilterCount}</span>
              )}
            </button>

            {showFilters && (
              <div className={styles.filterPanel}>
                <div className={styles.filterPanelHeader}>
                  <h3 className={styles.filterPanelTitle}>Filter Recipes</h3>
                  {activeFilterCount > 0 && (
                    <button className={styles.clearFiltersButton} onClick={clearAllFilters}>
                      Clear all
                    </button>
                  )}
                </div>

                <div className={styles.filterGroup}>
                  <label className={styles.filterLabel} htmlFor="cookbookFilter">Cookbook</label>
                  <select
                    id="cookbookFilter"
                    className={styles.filterSelect}
                    value={selectedCookbook}
                    onChange={(e) => setSelectedCookbook(e.target.value)}
                  >
                    <option value="">All cookbooks</option>
                    {cookbookOptions.map((cb) => (
                      <option key={cb} value={cb}>{cb}</option>
                    ))}
                  </select>
                </div>

                <div className={styles.filterGroup}>
                  <label className={styles.filterLabel} htmlFor="authorFilter">Author</label>
                  <select
                    id="authorFilter"
                    className={styles.filterSelect}
                    value={selectedAuthor}
                    onChange={(e) => setSelectedAuthor(e.target.value)}
                  >
                    <option value="">All authors</option>
                    {authorOptions.map((a) => (
                      <option key={a} value={a}>{a}</option>
                    ))}
                  </select>
                </div>

                <div className={styles.filterGroup}>
                  <label className={styles.filterLabel}>Review level</label>
                  <div className={styles.ratingOptions}>
                    {RATING_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        aria-pressed={minRating === opt.value}
                        className={`${styles.ratingOption} ${minRating === opt.value ? styles.ratingOptionActive : ''}`}
                        onClick={() => setMinRating(minRating === opt.value ? 0 : opt.value)}
                      >
                        <Star size={14} fill="currentColor" />
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className={styles.categories}>
          {CATEGORIES.map((category) => (
            <button
              key={category}
              className={`${styles.categoryButton} ${selectedCategory === category ? styles.categoryActive : ''}`}
              onClick={() => setSelectedCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.resultsInfo}>
        <span>{filteredRecipes.length} recipes found</span>
        {activeFilterCount > 0 && (
          <button className={styles.inlineClearButton} onClick={clearAllFilters}>
            <X size={16} />
            Clear filters
          </button>
        )}
      </div>

      <div className={styles.grid}>
        {visibleRecipes.map((recipe) => (
          <RecipeCard key={recipe.id} recipe={recipe} />
        ))}
      </div>

      {hasMore && (
        <div ref={loadMoreRef} className={styles.loadMoreSentinel}>
          <span className={styles.loadMoreText}>Loading more recipes…</span>
        </div>
      )}

      {filteredRecipes.length === 0 && (
        <div className={styles.emptyState}>
          <p>No recipes match your search. Try adjusting your filters.</p>
          {activeFilterCount > 0 && (
            <button className={styles.emptyClearButton} onClick={clearAllFilters}>
              Clear all filters
            </button>
          )}
        </div>
      )}

      {!hasFullAccess && recipes && recipes.length > FREE_USER_RECIPE_LIMIT && (
        <div className={styles.upgradePrompt}>
          <Lock size={24} />
          <div>
            <h3>Want access to all recipes?</h3>
            <p>Subscribe to unlock our complete recipe collection.</p>
          </div>
          <a href={SUBSCRIBE_URL} target="_blank" rel="noopener noreferrer" className={styles.upgradeButton}>
            Subscribe Now
          </a>
        </div>
      )}
    </div>
  );
}
